import React, { useState, useEffect, useRef } from 'react';
import { ref, onValue } from 'firebase/database';
import { database } from '../../firebase';
import MiniGamesChat from './MiniGamesChat';

import {
  ALL_SHIPS,
  ALL_WEATHERS,
  SPEED_MATRIX,
  CHIP_VALUES,
  MAX_BET_PER_USER,
  MAX_BET_PER_SHIP_PER_USER,
  MAX_SHIP_POOL,
  ZONE_WIDTH,
  DOCK_WIDTH,
  SHIP_START,
  computeShipPosition,
  formatSpeed,
  getShipGlobalIndex,
  getWeatherGlobalIndex,
  subscribeDrakkarRaceState,
  subscribeDrakkarPools,
  subscribeDrakkarHistory,
  subscribeDrakkarBettors,
  subscribeDrakkarPresence,
  updateDrakkarPresence,
  refreshDrakkarRace,
  placeDrakkarBet,
  incrementDrakkarPool
} from '../../services/miniGameService';
import './DrakkarRace.css';

const FINISH_LINE = DOCK_WIDTH + 5 * ZONE_WIDTH; // 100%
const DEFAULT_HOUSE_SEED = 500;
const DEFAULT_MULTIPLIER = 0.9;
const DURATIONS = {
  betting: 20000,
  reveal: 4000,
  racing: 0,
  result: 3000
};

const WEATHER_SVGS = {
  '☀️': <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>,
  '⚡': <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  '🌫️': <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="7" x2="20" y2="7"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="17" x2="20" y2="17"/></svg>,
  '🐙': <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a7 7 0 0 0-7 7c0 2.4 1.3 4.5 3.3 5.7M12 2a7 7 0 0 1 7 7c0 2.4-1.3 4.5-3.3 5.7M12 9v4M8 13v4M16 13v4M5 14v4M19 14v4M12 22v-3"/></svg>,
  '💨': <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.7 7.7A2.5 2.5 0 1 1 20 12H4M17.7 16.3A2.5 2.5 0 1 0 20 12"/></svg>,
  '🧊': <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0-6-8-10-8-10S4 4 4 10c0 6 8 10 8 10s8-4 8-10Z"/><path d="M12 20v2"/><path d="M12 8v1"/><path d="M12 13v1"/></svg>,
  '✨': <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg>
};

const DrakkarRace = ({ user, userPoints, setFrozen, setDisplayedPoints }) => {
  const [state, setState] = useState(null);
  const [pools, setPools] = useState({});
  const [history, setHistory] = useState([]);
  const [reactions, setReactions] = useState([]);
  const prevBettorsRef = useRef({});
  const [selectedChip, setSelectedChip] = useState(10);
  const [myBets, setMyBets] = useState({});
  const [pendingBetsTotal, setPendingBetsTotal] = useState(0);
  const [localError, setLocalError] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [showRules, setShowRules] = useState(false);
  const [presence, setPresence] = useState({});
  const [activeTab, setActiveTab] = useState('history'); // 'history' or 'chat'


  // Animation
  const animFrameRef = useRef();
  const shipRefs = useRef([]);
  const [shipPositions, setShipPositions] = useState([SHIP_START, SHIP_START, SHIP_START]);
  const [raceFinished, setRaceFinished] = useState(false);
  const [tickCount, setTickCount] = useState(0); // Forced repaint counter
  const prevRaceIdRef = useRef(null);

  // Bot Simulation State
  const [ghostPools, setGhostPools] = useState({});
  const processedBotActionsRef = useRef(new Set());

  const pendingRequestsRef = useRef(0);
  const pendingBatchRef = useRef({}); // { shipId: amount }
  const batchTimerRef = useRef(null);
  const [serverOffset, setServerOffset] = useState(0);
  const errorTimeoutRef = useRef(null);

  const triggerLocalError = (msg) => {
    setLocalError(msg);
    if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
    errorTimeoutRef.current = setTimeout(() => setLocalError(null), 2000);
  };

  // Animation Strategy: Use refs to prevent stale closures and competing loops
  const stateRef = useRef(null);
  const raceFinishedRef = useRef(false);
  const serverOffsetRef = useRef(0);

  useEffect(() => { stateRef.current = state; }, [state]);
  useEffect(() => { raceFinishedRef.current = raceFinished; }, [raceFinished]);
  useEffect(() => { serverOffsetRef.current = serverOffset; }, [serverOffset]);

  // ─── 0. Server Time Offset ───
  useEffect(() => {
    const offsetRef = ref(database, ".info/serverTimeOffset");
    const unsub = onValue(offsetRef, (snap) => {
      setServerOffset(snap.val() || 0);
    });
    return () => unsub();
  }, []);

  // ─── 1. Subscriptions ───
  useEffect(() => {
    const unsubState = subscribeDrakkarRaceState((newState) => {
      setState(newState);
    });
    const unsubPools = subscribeDrakkarPools((newPools) => {
      setPools(newPools);
    });
    const unsubHistory = subscribeDrakkarHistory((entries) => {
      setHistory(entries);
    });

    return () => {
      unsubState();
      unsubPools();
      unsubHistory();
    };
  }, []);

  // ─── 2. Social Proof ───
  useEffect(() => {
    const unsubBettors = subscribeDrakkarBettors((newBettors) => {
      // Detect new bets for live "floating" reactions
      const prev = prevBettorsRef.current;
      Object.keys(newBettors).forEach(shipId => {
        const shipBettors = newBettors[shipId];
        Object.keys(shipBettors).forEach(uid => {
          // If this user wasn't in the previous list for this ship, or they were but the data is fresh
          // Note: In RTDB, we don't have the amount easily here unless we add it, but we can animate the avatar
          if (!prev[shipId] || !prev[shipId][uid]) {
            const reactionId = `react-${shipId}-${uid}-${Date.now()}`;
            setReactions(prevR => [
              ...prevR,
              { 
                id: reactionId, 
                shipId, 
                avatar: shipBettors[uid].avatar,
                name: shipBettors[uid].name
              }
            ]);
            // Auto-remove after 3 seconds
            setTimeout(() => {
              setReactions(prevR => prevR.filter(r => r.id !== reactionId));
            }, 3000);
          }
        });
      });
      prevBettorsRef.current = newBettors;
    });
    const unsubPresence = subscribeDrakkarPresence(setPresence);
    return () => {
      unsubBettors();
      unsubPresence();
    };
  }, []);

  useEffect(() => {
    if (user?.uid) {
      updateDrakkarPresence(user.uid);
    }
  }, [user?.uid]);

  // Reset bets when a new race starts
  useEffect(() => {
    if (state && state.raceId !== prevRaceIdRef.current) {
      prevRaceIdRef.current = state.raceId;
      setMyBets({});
      setLocalError(null);
      setRaceFinished(false);
      setGhostPools({});
      processedBotActionsRef.current = new Set();
      
      // Safety Reset for Sync Lock
      pendingRequestsRef.current = 0;
      setPendingBetsTotal(0);
      setFrozen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.raceId]);

  // ─── 2. Heartbeat & Timer ───
  useEffect(() => {
    let nullWaitTicks = 0;
    const interval = setInterval(() => {
      if (!state) {
        nullWaitTicks++;
        // If state is null for 3 seconds (6 ticks), attempt to revive/initialize the race
        if (nullWaitTicks > 6) {
          refreshDrakkarRace();
          nullWaitTicks = 0;
        }
        return;
      }
      
      nullWaitTicks = 0;
      const now = Date.now();
      const diff = state.endTime - now;
      setTimeLeft(Math.max(0, diff));

      if (diff <= 0) {
        refreshDrakkarRace();
      }
    }, 500);

    return () => clearInterval(interval);
  }, [state]);

  // ─── 2.1 Bot Simulation Playback ───
  useEffect(() => {
    if (!state || state.phase !== 'betting' || !state.botActions) return;

    const interval = setInterval(() => {
      const now = Date.now() + serverOffset;
      const startTime = state.lastUpdate || (state.endTime - DURATIONS.betting);
      const elapsed = now - startTime;

      state.botActions.forEach(action => {
        if (elapsed >= action.timeOffset && !processedBotActionsRef.current.has(action.id)) {
          processedBotActionsRef.current.add(action.id);
          
          // 1. Update visual pool
          setGhostPools(prev => ({
            ...prev,
            [action.shipId]: (prev[action.shipId] || 0) + action.amount
          }));

          // 2. Trigger reaction
          const reactionId = `bot-react-${action.id}`;
          setReactions(prevR => [
            ...prevR,
            {
              id: reactionId,
              shipId: action.shipId,
              isBot: true,
              initial: action.initial,
              color: action.color
            }
          ]);
          setTimeout(() => {
            setReactions(prevR => prevR.filter(r => r.id !== reactionId));
          }, 3000);
        }
      });
    }, 500);

    return () => clearInterval(interval);
  }, [state, serverOffset]);

  // ─── 3. Stable Animation Engine ───
  useEffect(() => {
    const tick = () => {
      const s = stateRef.current;
      const rf = raceFinishedRef.current;
      const offset = serverOffsetRef.current;

      if (!s || rf) {
        animFrameRef.current = requestAnimationFrame(tick);
        return;
      }

      // ─── 3.1 Racing or Result Phase ───
      if (s.phase === 'racing' || s.phase === 'result') {
        if (!s.shipIndices || !s.weatherIndices) {
          animFrameRef.current = requestAnimationFrame(tick);
          return;
        }

        const now = Date.now() + offset;
        const duration = s.raceDuration || 25000;
        const serverStart = s.raceStartTime || (s.endTime - duration);

        if (!serverStart) {
          animFrameRef.current = requestAnimationFrame(tick);
          return;
        }

        const elapsed = now - serverStart;
        const winnerFinishTime = s.finishTimes ? s.finishTimes[s.winnerIdx] : 999999;

        // Finish Condition
        if (elapsed >= winnerFinishTime) {
          const finalPositions = s.shipIndices.map((sIdx, i) => {
            if (i === s.winnerIdx) return FINISH_LINE;
            const shipTotalTime = s.finishTimes[i];
            const shipSpeeds = s.weatherIndices.map((wIdx) => SPEED_MATRIX[sIdx][wIdx]);
            return computeShipPosition(shipSpeeds, Math.min(winnerFinishTime, shipTotalTime));
          });

          // Direct-DOM Final Snap
          finalPositions.forEach((pos, i) => {
            if (shipRefs.current[i]) {
              shipRefs.current[i].style.setProperty('--dv2-ship-pos', `${pos}%`);
            }
          });

          setShipPositions(finalPositions);
          setRaceFinished(true); // Triggers re-render and ref update
          animFrameRef.current = requestAnimationFrame(tick);
          return;
        }

        // Live Movement
        const newPositions = s.shipIndices.map((sIdx) => {
          const shipSpeeds = s.weatherIndices.map((wIdx) => SPEED_MATRIX[sIdx][wIdx]);
          return computeShipPosition(shipSpeeds, elapsed);
        });
        
        // Direct-DOM Update for perfectly smooth 60fps movement
        newPositions.forEach((pos, i) => {
          if (shipRefs.current[i]) {
            shipRefs.current[i].style.setProperty('--dv2-ship-pos', `${pos}%`);
          }
        });

        setShipPositions(newPositions);
        setTickCount(c => (c + 1) % 1000); // Pulse a re-render

        // Deep Diagnostic Log
        if (Math.random() < 0.05) {
          console.log(`Drakkar Engine v2.11 | Race#${s.raceId} | Elapsed: ${elapsed}ms | Positions:`, newPositions);
        }
      } else {
        // Idle / Betting Phase: Clear Direct-DOM variables to avoid "stuck" visuals
        shipRefs.current.forEach(ref => {
          if (ref) ref.style.removeProperty('--dv2-ship-pos');
        });

        setShipPositions([SHIP_START, SHIP_START, SHIP_START]);
        setRaceFinished(false);
      }

      animFrameRef.current = requestAnimationFrame(tick);
    };

    animFrameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, []); // Run only once on mount

  // ─── 4. Betting Logic (Redesigned with Batching) ───
  const sendBatchToServer = async () => {
    const currentBatch = { ...pendingBatchRef.current };
    // Clear batch ref immediately so new clicks start a new batch
    pendingBatchRef.current = {};
    if (batchTimerRef.current) clearTimeout(batchTimerRef.current);
    batchTimerRef.current = null;

    const shipIds = Object.keys(currentBatch);
    if (shipIds.length === 0) return;

    // Track active network requests correctly
    pendingRequestsRef.current += shipIds.length;

    // Process all ships in the batch in parallel
    await Promise.all(shipIds.map(async (shipId) => {
      const amount = currentBatch[shipId];
      try {
        const result = await placeDrakkarBet(shipId, amount);
        if (result.success) {
          // If this was the last in-flight batch, sync confirmed balance
          if (pendingRequestsRef.current === 1) {
            setDisplayedPoints(result.newBalance);
          }
        } else {
          // REVERT: Request was rejected by server (e.g. out of funds/time)
          setMyBets(prev => ({ ...prev, [shipId]: Math.max(0, (prev[shipId] || 0) - amount) }));
          setDisplayedPoints(prev => prev + amount); 
          await incrementDrakkarPool(shipId, -amount);
          triggerLocalError(result.error);
        }
      } catch (err) {
        // EMERGENCY REVERT
        setMyBets(prev => ({ ...prev, [shipId]: Math.max(0, (prev[shipId] || 0) - amount) }));
        setDisplayedPoints(prev => prev + amount);
        await incrementDrakkarPool(shipId, -amount);
        triggerLocalError(err.message);
      } finally {
        pendingRequestsRef.current = Math.max(0, pendingRequestsRef.current - 1);
        if (pendingRequestsRef.current === 0) {
          setFrozen(false);
        }
      }
    }));
  };

  const handlePlaceBet = async (shipId) => {
    // Phase check (server has 2s grace for late packets)
    if (state?.phase !== 'betting' || timeLeft < 500) {
      if (state?.phase !== 'betting') triggerLocalError("Betting is currently closed.");
      return;
    }
    setLocalError(null);

    const amount = selectedChip === 'MAX' ? 0 : selectedChip; 
    const currentTotal = Object.values(myBets).reduce((a, b) => a + b, 0);
    const shipBet = myBets[shipId] || 0;
    const shipPool = pools[shipId] || 0;
    
    // ── STEP 0: Calculate Actual Amount for 'MAX' ──
    let finalAmount = amount;
    if (selectedChip === 'MAX') {
      const remainingUserCap = MAX_BET_PER_USER - currentTotal - pendingBetsTotal;
      const remainingShipCap = MAX_BET_PER_SHIP_PER_USER - shipBet;
      const remainingPoolCap = MAX_SHIP_POOL - shipPool;
      
      finalAmount = Math.min(
        userPoints,
        remainingUserCap,
        remainingShipCap,
        remainingPoolCap
      );
    }

    if (finalAmount <= 0) {
      if (selectedChip === 'MAX') triggerLocalError("You've reached your bet limit or are out of funds.");
      return;
    }

    // Safety checks
    if (shipPool + finalAmount > MAX_SHIP_POOL) {
      triggerLocalError("This ship is full!");
      return;
    }
    if (shipBet + finalAmount > MAX_BET_PER_SHIP_PER_USER) {
      triggerLocalError(`Max bet per ship is ${MAX_BET_PER_SHIP_PER_USER}.`);
      return;
    }
    if (currentTotal + pendingBetsTotal + finalAmount > MAX_BET_PER_USER) {
      triggerLocalError(`Max bet is ${MAX_BET_PER_USER} / race.`);
      return;
    }
    if (finalAmount > userPoints) {
      triggerLocalError(`Insufficient Valcoins.`);
      return;
    }

    // ── STEP 1: HYPER-INSTANT VISUALS (Feel) ──
    setFrozen(true);
    setMyBets(prev => ({ ...prev, [shipId]: (prev[shipId] || 0) + finalAmount }));
    setPendingBetsTotal(prev => prev + finalAmount);
    setDisplayedPoints(prev => prev - finalAmount); // Optimistic Balance
    
    // Global Pool increment is now TRULY instant for everyone
    incrementDrakkarPool(shipId, finalAmount); 
    
    // ── STEP 2: ADD TO BATCH ──
    pendingBatchRef.current[shipId] = (pendingBatchRef.current[shipId] || 0) + finalAmount;
    
    // ── STEP 3: DEBOUNCED FLUSH (250ms) ──
    if (batchTimerRef.current) clearTimeout(batchTimerRef.current);
    batchTimerRef.current = setTimeout(sendBatchToServer, 250);
  };

  // ─── Derived Data ───
  const raceShips = state?.ships || [];
  const raceWeathers = state?.weathers || [];

  const currentHouseSeed = state?.houseSeed ?? DEFAULT_HOUSE_SEED;
  const currentMultiplier = state?.multiplierFactor ?? DEFAULT_MULTIPLIER;
  const highestProgress = Math.max(...shipPositions);

  const totalPool = raceShips.reduce((sum, s) => {
    const rtdbPool = pools[s.id] || 0;
    const ghostPool = ghostPools[s.id] || 0;
    return sum + rtdbPool + ghostPool;
  }, 0);

  const isSyncing = (state?.phase !== 'betting') && (pendingRequestsRef.current > 0);

  const getPhaseLabel = () => {
    if (!state) return '';
    switch (state.phase) {
      case 'betting': 
        return timeLeft < 500 ? '🔒 LOCKING BETS...' : '⚓ PLACE YOUR BETS';
      case 'reveal': return '🌊 WEATHERS REVEALED';
      case 'racing': return '⛵ RACE IN PROGRESS';
      case 'result': return '🏅 WINNER REVEALED';
      default: return '';
    }
  };

  const getEstimatedPayout = (shipId) => {
    const rtdbPool = pools[shipId] || 0;
    const ghostPool = ghostPools[shipId] || 0;
    const shipPool = rtdbPool + ghostPool;
    if (shipPool === 0 || totalPool === 0) return '—';
    const multiplier = (totalPool / shipPool) * currentMultiplier;
    return multiplier.toFixed(1) + 'x';
  };

  // ─── Loading ───
  if (!state) return <div className="minigames-loading"><div className="minigames-spinner" /></div>;

  return (
    <div className="drakkar-v2-container">
      {/* ═══ SYNC LOCK OVERLAY ═══ */}
      {isSyncing && (
        <div className="dv2-sync-lock">
          <div className="dv2-sync-content">
            <div className="dv2-spinner" />
            <h3>Finalizing Your Bets...</h3>
            <p>Wait a moment for all transactions to synchronize.</p>
          </div>
        </div>
      )}

      {/* ═══ STATUS BAR ═══ */}
      <div className="dv2-status-bar">
        <div className="dv2-status-left">
          <span className="dv2-players-count">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '4px'}}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            {(Object.keys(presence).length) + (state?.botCount || 0)} <span className="dv2-players-label">Players</span>
          </span>
          <span className="dv2-phase-label">
            {getPhaseLabel()}
          </span>
          <span className="dv2-race-id"><span className="dv2-race-label">Race</span> #{state.raceId || 0}</span>
        </div>
        <div className="dv2-status-right">
          <button className="dv2-rules-btn" onClick={() => setShowRules(true)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '6px'}}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
            <span className="dv2-btn-text">Rules</span>
          </button>
          <div className="dv2-timer-pill">
            {state.phase === 'racing' ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>
            ) :
              state.phase === 'betting' ? (
                <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  {Math.ceil(timeLeft / 1000)}s
                </div>
              ) :
                <div className="dv2-spinner" />}
          </div>
        </div>
      </div>

      {localError && <div className="dv2-error">{localError}</div>}

      {/* ═══ MAIN LAYOUT: History | Track+Betting ═══ */}
      <div className="dv2-main-layout">

        {/* ── TRACK & BETTING ── */}
        <div className="dv2-race-section">

          {/* ═══ WEATHER BAR ═══ */}
          <div className="dv2-weather-bar">
            <div className="dv2-weather-dock" style={{ width: `${DOCK_WIDTH}%` }}>
              <span className="dv2-dock-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18"/><path d="M10 21V7a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v14"/><path d="M18 21V11a1 1 0 0 0-1-1h-2a1 1 0 0 0-1 1v10"/><path d="M6 21V15a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v6"/></svg>
              </span>
              <span className="dv2-dock-text">Dock</span>
            </div>
            {raceWeathers.map((w, i) => {
              const isHidden = state.phase === 'betting' && i !== state.revealedIndex;
              const isTouched = highestProgress >= (DOCK_WIDTH + i * ZONE_WIDTH);
              const statusClass = state.phase === 'racing' || state.phase === 'result'
                ? (isTouched ? 'zone-active' : 'zone-untouched')
                : '';

              return (
                <div
                  key={i}
                  className={`dv2-weather-zone ${isHidden ? 'hidden' : ''} ${statusClass}`}
                >
                  <span className="dv2-weather-icon">
                    {isHidden ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                    ) : WEATHER_SVGS[w.icon] || w.icon}
                  </span>
                  <span className="dv2-weather-name">
                    {isHidden ? 'Secret' : w.name}
                  </span>
                </div>
              );
            })}
          </div>

          {/* ═══ RACE TRACK ═══ */}
          <div className="dv2-track-area">
            <div className="dv2-track-water" />

            {/* Weather Zone Tints */}
            {raceWeathers.map((w, i) => {
              const isHidden = state.phase === 'betting' && i !== state.revealedIndex;
              const isTouched = highestProgress >= (DOCK_WIDTH + i * ZONE_WIDTH);
              const statusClass = state.phase === 'racing' || state.phase === 'result'
                ? (isTouched ? 'zone-active' : 'zone-untouched')
                : '';

              return (
                <div
                  key={i}
                  className={`dv2-zone-tint ${statusClass}`}
                  style={{
                    left: `${DOCK_WIDTH + i * ZONE_WIDTH}%`,
                    width: `${ZONE_WIDTH}%`,
                    background: isHidden ? 'transparent' : `${w.color}CC` // CC = approx 0.8 opacity solid
                  }}
                />
              );
            })}

            {/* Dashed zone dividers */}
            <div className="dv2-zone-divider" style={{ left: `${DOCK_WIDTH}%` }} />
            <div className="dv2-zone-divider" style={{ left: `${DOCK_WIDTH + ZONE_WIDTH}%` }} />
            <div className="dv2-zone-divider" style={{ left: `${DOCK_WIDTH + 2 * ZONE_WIDTH}%` }} />
            <div className="dv2-zone-divider" style={{ left: `${DOCK_WIDTH + 3 * ZONE_WIDTH}%` }} />
            <div className="dv2-zone-divider" style={{ left: `${DOCK_WIDTH + 4 * ZONE_WIDTH}%` }} />
            <div className="dv2-finish-line" />

            {/* Ship Lanes */}
            <div className="dv2-lanes">
              {raceShips.map((ship, i) => (
                <div key={ship.id} className="dv2-lane">
                  <div className="dv2-lane-label" style={{ color: ship.color }}>{ship.name}</div>
                  <div
                    ref={el => shipRefs.current[i] = el}
                    className={`dv2-ship-wrapper ${state.phase === 'racing' ? 'racing' : ''} ${state.phase === 'result' && state.winnerIdx === i ? 'winner' : ''}`}
                    style={{
                      // Nose-Centric Positioning: Priority to CSS Variable for Direct-DOM performance
                      left: `calc(var(--dv2-ship-pos, ${shipPositions[i]}%) + 1.5% - var(--dv2-ship-width))`,
                    }}
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/icons/minigames/ships/${ship.id}.png`}
                      alt={ship.name}
                      className="dv2-ship-img"
                    />
                    {state.phase === 'racing' && (
                      <div className={`dv2-ship-speed speed-tag-${SPEED_MATRIX[getShipGlobalIndex(ship.id)][state.weatherIndices[Math.max(0, Math.min(4, Math.floor((shipPositions[i] - DOCK_WIDTH) / ZONE_WIDTH)))]]
                        }`}>
                        {raceWeathers[Math.max(0, Math.min(4, Math.floor((shipPositions[i] - DOCK_WIDTH) / ZONE_WIDTH)))]?.icon && 
                          WEATHER_SVGS[raceWeathers[Math.max(0, Math.min(4, Math.floor((shipPositions[i] - DOCK_WIDTH) / ZONE_WIDTH)))]?.icon]
                        } 
                        {formatSpeed(SPEED_MATRIX[getShipGlobalIndex(ship.id)][state.weatherIndices[Math.max(0, Math.min(4, Math.floor((shipPositions[i] - DOCK_WIDTH) / ZONE_WIDTH)))]])}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ═══ RESULT OVERLAY ═══ */}
          {state.phase === 'result' && state.winnerIdx !== null && raceShips[state.winnerIdx] && (
            <div className="dv2-result-banner">
              <span className="dv2-result-crown">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'var(--accent-gold)'}}><path d="m2 4 3 12h14l3-12-6 7-4-7-4 7-6-7zm3 16h14"/></svg>
              </span>
              <span className="dv2-result-name" style={{ color: raceShips[state.winnerIdx].color }}>
                {raceShips[state.winnerIdx].name} Wins!
              </span>
              {totalPool > 0 && (
                <span className="dv2-result-payout">
                  Payout: {getEstimatedPayout(raceShips[state.winnerIdx].id)}
                </span>
              )}
            </div>
          )}

          {/* ═══ BETTING SECTION ═══ */}
          <div className="dv2-betting-section">
            {/* Chip Selector */}
            <div className="dv2-chip-selector">
              <span className="dv2-chip-label">Bet Amount:</span>
              {CHIP_VALUES.map(chip => (
                <button
                  key={chip}
                  className={`dv2-chip ${selectedChip === chip ? 'active' : ''}`}
                  onClick={() => setSelectedChip(chip)}
                >
                  ×{chip}
                </button>
              ))}
              <button
                className={`dv2-chip dv2-chip-max ${selectedChip === 'MAX' ? 'active' : ''}`}
                onClick={() => setSelectedChip('MAX')}
              >
                MAX
              </button>
            </div>

            {/* Ship Betting Cards */}
            <div className="dv2-bet-cards">
              {raceShips.map((ship) => {
                const shipGlobalIdx = getShipGlobalIndex(ship.id);
                const revealedIdx = (state.revealedIndex !== undefined && raceWeathers[state.revealedIndex])
                  ? getWeatherGlobalIndex(raceWeathers[state.revealedIndex].id)
                  : -1;
                const revealedWeatherSpeed = revealedIdx >= 0 && shipGlobalIdx >= 0
                  ? SPEED_MATRIX[shipGlobalIdx][revealedIdx]
                  : null;

                const rtdbPool = pools[ship.id] || 0;
                const ghostPool = ghostPools[ship.id] || 0;
                const shipPool = rtdbPool + ghostPool;
                const isFull = shipPool >= MAX_SHIP_POOL;

                return (
                  <div
                    key={ship.id}
                    className={`dv2-bet-card ${state.phase !== 'betting' || isFull ? 'disabled' : ''} ${isFull ? 'full' : ''}`}
                    style={{ '--ship-accent': ship.color }}
                    onClick={() => {
                      if (state.phase === 'betting' && !isFull) {
                        handlePlaceBet(ship.id);
                      }
                    }}
                  >
                    {isFull && <div className="dv2-full-badge">FULL</div>}
                    {/* Floating Reactions */}
                    <div className="dv2-reactions-container">
                      {reactions.filter(r => r.shipId === ship.id).map(r => (
                        <div key={r.id} className="dv2-floating-reaction">
                          {r.isBot ? (
                            <div 
                              className="dv2-bot-avatar" 
                              style={{ backgroundColor: r.color }}
                            >
                              {r.initial}
                            </div>
                          ) : (
                            <img src={r.avatar || `${process.env.PUBLIC_URL}/icons/default_avatar.png`} alt="" />
                          )}
                          <span className="dv2-reaction-plus">+Bet</span>
                        </div>
                      ))}
                    </div>
                    <div className="dv2-bet-card-top">
                      <img
                        src={`${process.env.PUBLIC_URL}/icons/minigames/ships/${ship.id}.png`}
                        alt={ship.name}
                        className="dv2-bet-card-ship"
                      />
                      <div className="dv2-bet-card-info">
                        <h4 style={{ color: ship.color }}>{ship.name}</h4>
                        {revealedWeatherSpeed !== null && (
                          <span className="dv2-speed-hint">
                            <>
                              {WEATHER_SVGS[raceWeathers[state.revealedIndex]?.icon] || raceWeathers[state.revealedIndex]?.icon}
                              <span>{formatSpeed(revealedWeatherSpeed)}</span>
                            </>
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="dv2-bet-card-pools">
                      <div className="dv2-pool-row">
                        <span>Global Pool</span>
                        <span className="dv2-pool-amount">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '2px'}}><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/></svg>
                          {Math.floor((pools[ship.id] || 0) + (ghostPools[ship.id] || 0))}
                        </span>
                      </div>
                      <div className="dv2-pool-row">
                        <span>Your Bet</span>
                        <span className="dv2-pool-amount dv2-my-bet">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '2px'}}><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/></svg>
                          {myBets[ship.id] || 0}
                        </span>
                      </div>
                      <div className="dv2-pool-row">
                        <span>Est. Payout</span>
                        <span className="dv2-pool-amount dv2-payout">{getEstimatedPayout(ship.id)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Totals */}
            <div className="dv2-totals-bar">
              <span>Total Pool: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{display: 'inline', verticalAlign: 'middle', marginRight: '4px'}}><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/></svg> {totalPool}</span>
              <span>Your Total: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{display: 'inline', verticalAlign: 'middle', marginRight: '4px'}}><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/></svg> {Object.values(myBets).reduce((a, b) => a + b, 0)} / {MAX_BET_PER_USER}</span>
            </div>
          </div>
        </div>

        {/* ── BOTTOM: Race History & Chat ── */}
        <div className="dv2-history-panel">
          <div className="dv2-tabs">
            <button 
              className={`dv2-tab ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => setActiveTab('history')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 8V4H8"/><rect width="16" height="12" x="4" y="8" rx="2"/><path d="M2 14h2"/><path d="M20 14h2"/><path d="M15 13v2"/><path d="M9 13v2"/></svg>
              History
            </button>
            <button 
              className={`dv2-tab ${activeTab === 'chat' ? 'active' : ''}`}
              onClick={() => setActiveTab('chat')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              Race Chat
            </button>
          </div>

          <div className="dv2-tab-content">
            {activeTab === 'history' ? (
              <div className="dv2-history-list">
                {history.length === 0 ? (
                  <div className="dv2-history-empty">No races yet</div>
                ) : (
                  history.map((entry, idx) => (
                    <div key={idx} className="dv2-history-item">
                      <div className="dv2-history-winner-row">
                        <img
                          src={`${process.env.PUBLIC_URL}/icons/minigames/ships/${entry.winner?.id}.png`}
                          alt=""
                          className="dv2-history-ship-icon"
                          style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }}
                        />
                        <span className="dv2-history-ship-name" style={{ color: entry.winner?.color }}>
                          {entry.winner?.name || '???'}
                        </span>
                      </div>
                      <div className="dv2-history-details">
                        <span style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/></svg>
                          {entry.totalPool || 0}
                        </span>
                        <span className="dv2-history-multiplier">{entry.payoutMultiplier?.toFixed(1) || '—'}x</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            ) : (
              <div className="dv2-chat-wrapper">
                <MiniGamesChat user={user} channelId="drakkar" integrated={true} />
              </div>
            )}
          </div>
        </div>

      </div>

      {/* ═══ RULES MODAL ═══ */}
      {showRules && (
        <div className="dv2-rules-overlay" onClick={() => setShowRules(false)}>
          <div className="dv2-rules-modal" onClick={e => e.stopPropagation()}>
            <div className="dv2-rules-header">
              <h2>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '12px'}}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
                Drakkar Race Rules
              </h2>
              <button className="dv2-rules-close" onClick={() => setShowRules(false)}>✕</button>
            </div>
            <div className="dv2-rules-body">
              <h3>Speed Multiplier Table</h3>
              <p>Each ship has strengths and weaknesses across 7 weather types:</p>
              <div className="dv2-rules-table-wrapper">
                <table className="dv2-speed-table">
                  <thead>
                    <tr>
                      <th>Ship</th>
                      {ALL_WEATHERS.map(w => (
                        <th key={w.id}>
                          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px'}}>
                            {WEATHER_SVGS[w.icon] || w.icon}
                            <small>{w.name}</small>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ALL_SHIPS.map((ship, sIdx) => (
                      <tr key={ship.id}>
                        <td style={{ color: ship.color, fontWeight: 700 }}>{ship.name}</td>
                        {ALL_WEATHERS.map((_, wIdx) => {
                          const val = SPEED_MATRIX[sIdx][wIdx];
                          const isBest = val === 13;
                          const isWorst = val === 5;
                          return (
                            <td
                              key={wIdx}
                              className={isBest ? 'speed-best' : isWorst ? 'speed-worst' : ''}
                            >
                              {formatSpeed(val)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h3>How It Works</h3>
              <p>3 random ships and <strong>5 random weather zones</strong> are selected for each race. Only 1 weather zone is revealed during betting — the other 4 zones remain hidden until the race begins! Each zone affects a ship's speed based on its unique stats.</p>

              <h3>Parimutuel Payout</h3>
              <p>Drakkar Race uses a <strong>Parimutuel Payout</strong> system where total bets are shared among winners. To ensure high payouts even for solo players:</p>
              <ul>
                <li>The House seeds <strong>{currentHouseSeed} Valcoins</strong> on each ship (Total {currentHouseSeed * 3} seed per race).</li>
                <li><strong>Total Pool</strong> = All player bets + {currentHouseSeed * 3} House Seed.</li>
                <li><strong>Multiplier</strong> = (Total Pool ÷ Winning Ship's Total Pool) × {currentMultiplier.toFixed(2)}</li>
                <li>Wins that <strong>double your total investment (2x+)</strong> trigger a Global Win Announcement!</li>
              </ul>

              <div className="dv2-formula" style={{ margin: '15px 0', background: 'rgba(0,0,0,0.4)', padding: '15px', borderRadius: '8px', textAlign: 'center', fontWeight: 'bold' }}>
                Your Winning = (Your Bet ÷ Winning Ship Pool) × Total Pool × {currentMultiplier.toFixed(2)}
              </div>
              <p style={{ marginTop: '10px', fontSize: '0.9em', opacity: 0.8 }}>
                The house takes a {Math.round((1 - currentMultiplier) * 100)}% fee. You swipe the House's seed if you are the only one who correctly predicts the winner!
              </p>

              <h3>Betting Rules</h3>
              <ul>
                <li>Max bet per user / ship: <strong>{MAX_BET_PER_SHIP_PER_USER} Valcoins</strong> (Total {MAX_BET_PER_USER})</li>
                <li>Max total pool per ship: <strong>{MAX_SHIP_POOL.toLocaleString()} Valcoins</strong></li>
                <li>You can bet on multiple ships</li>
                <li>Select a chip value and click a ship to bet</li>
                <li>Bets are deducted from your balance immediately</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Version Stamp for Diagnostic */}
      <div style={{
        position: 'absolute',
        bottom: '10px',
        right: '20px',
        fontSize: '10px',
        color: 'rgba(255,255,255,0.4)',
        pointerEvents: 'none',
        zIndex: 1000
      }}>
        v2.12.Final [{tickCount}]
      </div>
    </div>
  );
};

export default DrakkarRace;
